/**
 * TransactionListPage (홈 화면)
 *
 * 월별 거래 목록을 조회하고 표시하는 페이지
 * - 리스트형/달력형 뷰 전환
 */

import { useMemo, useState, useEffect, useCallback } from 'react'
import { useTransactions } from '../hooks/useTransactions'
import { useMonthNavigation } from '../hooks/useMonthNavigation'
import { MonthNavigation } from '../components/MonthNavigation'
import { TransactionList } from '../components/TransactionList'
import { TransactionCalendar } from '../components/TransactionCalendar'
import { DailyTransactionsModal } from '../components/DailyTransactionsModal'
import { TransactionFilterModal } from '../components/TransactionFilterModal'
import { TransactionDetailModal } from '../components/TransactionDetailModal'
import { isMockMode } from '@/core/config/env'
import { STORAGE_KEYS } from '@/core/config/constants'
import type { TransactionFilter, Transaction } from '../types/Transaction'
import type { TransactionDisplayMode } from '../types/TransactionDisplayMode'
import { DEFAULT_DISPLAY_MODE } from '../types/TransactionDisplayMode'
import { usePaymentMethods } from '@/domains/payment/hooks/usePaymentMethods'
import { useUsers } from '@/domains/user/hooks/useUsers'
import { useCategories } from '@/domains/category/hooks/useCategories'
import type { Category } from '@/domains/category/types/Category'

type ViewMode = 'list' | 'calendar'

export function TransactionListPage() {
  // 뷰 모드 상태
  const [viewMode, setViewMode] = useState<ViewMode>('list')

  // 요약 카드 접기/펼치기 상태 (localStorage에서 초기값 로드)
  const [isSummaryExpanded, setIsSummaryExpanded] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.TRANSACTION_SUMMARY_EXPANDED)
    return saved ? JSON.parse(saved) : false // 기본: 접힌 상태
  })

  // 달력 날짜 클릭 모달 상태
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedDateTransactions, setSelectedDateTransactions] = useState<Transaction[]>([])

  // 거래 상세 모달 상태
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)

  // 거래 내역 표시 방식 (localStorage에서 초기값 로드)
  const [displayMode] = useState<TransactionDisplayMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.TRANSACTION_DISPLAY_MODE)
    return (saved === 'compact' || saved === 'full') ? saved : DEFAULT_DISPLAY_MODE
  })

  // 요약 카드 상태를 localStorage에 저장
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.TRANSACTION_SUMMARY_EXPANDED, JSON.stringify(isSummaryExpanded))
  }, [isSummaryExpanded])

  // 월 네비게이션
  const { year, month, goToPrevMonth, goToNextMonth } = useMonthNavigation() // 초기값: 현재 월

  // 필터 모달 상태
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [userFilter, setUserFilter] = useState<TransactionFilter>({})

  // 월 필터와 사용자 필터 결합
  const combinedFilter = useMemo(() => {
    const startDate = `${year}-${String(month).padStart(2, '0')}-01`
    const endDate = `${year}-${String(month).padStart(2, '0')}-31`

    return {
      ...userFilter,
      startDate: userFilter.startDate || startDate,
      endDate: userFilter.endDate || endDate,
    }
  }, [year, month, userFilter])

  /**
   * 거래 및 관련 데이터 병렬 로딩
   *
   * @performance 페이지 레벨에서 모든 데이터를 병렬로 로딩하여 전체 로딩 시간 단축
   * TransactionList 내부에서 Hook을 호출하면 순차 로딩되어 느려짐
   */
  const { transactions, loading: txLoading, error: txError } = useTransactions(combinedFilter)
  const { paymentMethods, loading: pmLoading, error: pmError } = usePaymentMethods()
  const { users, loading: usersLoading, error: usersError } = useUsers()
  const {
    expenseCategories,
    incomeCategories,
    loading: catLoading,
    error: catError
  } = useCategories()

  // 모든 로딩 상태 통합
  const loading = txLoading || pmLoading || usersLoading || catLoading

  // 에러 상태 통합
  const error = txError || pmError || usersError || catError

  // 거래가 이미 필터링되어 오므로 추가 필터링 불필요
  const currentMonthTransactions = transactions

  /**
   * 카테고리 flat 배열 생성 (대분류 + 소분류)
   *
   * @performance TransactionList에서 이동하여 페이지 레벨에서 계산
   * TransactionList 내부에서 Hook을 호출하면 순차 로딩되어 느려짐
   */
  const allCategories = useMemo(() => {
    const result: Category[] = []
    for (const major of [...expenseCategories, ...incomeCategories]) {
      result.push(major)
      if (major.children) {
        result.push(...major.children)
      }
    }
    return result
  }, [expenseCategories, incomeCategories])

  /**
   * 수입/지출 합계 계산
   *
   * @performance useMemo로 메모이제이션하여 불필요한 재계산 방지
   * currentMonthTransactions가 변경될 때만 재계산
   */
  const totalIncome = useMemo(() =>
    currentMonthTransactions
      .filter((tx) => tx.type === 'income')
      .reduce((sum, tx) => sum + tx.amount, 0),
    [currentMonthTransactions]
  )

  const totalExpense = useMemo(() =>
    currentMonthTransactions
      .filter((tx) => tx.type === 'expense')
      .reduce((sum, tx) => sum + tx.amount, 0),
    [currentMonthTransactions]
  )

  const balance = useMemo(() =>
    totalIncome - totalExpense,
    [totalIncome, totalExpense]
  )

  // 필터 적용 여부 확인
  const hasActiveFilters = useMemo(() => {
    return (
      !!userFilter.type ||
      (userFilter.majorCategoryIds && userFilter.majorCategoryIds.length > 0) ||
      (userFilter.paymentMethodIds && userFilter.paymentMethodIds.length > 0) ||
      (userFilter.spenderIds && userFilter.spenderIds.length > 0) ||
      !!userFilter.startDate ||
      !!userFilter.endDate
    )
  }, [userFilter])

  /**
   * 요약 카드 접기/펼치기 토글
   *
   * @performance useCallback으로 메모이제이션하여 자식 컴포넌트 리렌더 방지
   */
  const toggleSummary = useCallback(() => {
    setIsSummaryExpanded(prev => !prev)
  }, [])

  /**
   * 달력 날짜 클릭 핸들러
   *
   * @performance useCallback으로 메모이제이션하여 자식 컴포넌트 리렌더 방지
   */
  const handleDateClick = useCallback((date: Date, dayTransactions: Transaction[]) => {
    setSelectedDate(date)
    setSelectedDateTransactions(dayTransactions)
  }, [])

  /**
   * 거래 롱프레스 핸들러
   *
   * @performance useCallback으로 메모이제이션하여 자식 컴포넌트 리렌더 방지
   */
  const handleTransactionLongPress = useCallback((id: string) => {
    const transaction = currentMonthTransactions.find((tx) => tx.id === id)
    if (transaction) {
      setSelectedTransaction(transaction)
      setIsDetailModalOpen(true)
    }
  }, [currentMonthTransactions])

  /**
   * 거래 상세 모달 닫기 핸들러
   */
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false)
    setSelectedTransaction(null)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* 월 네비게이션 */}
      <MonthNavigation
        year={year}
        month={month}
        onPrevMonth={goToPrevMonth}
        onNextMonth={goToNextMonth}
        onFilterClick={() => setShowFilterModal(true)}
        hasActiveFilters={hasActiveFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* 월별 요약 카드 - 접기/펼치기 */}
      <div className="px-4 py-2">
        <div className="max-w-4xl mx-auto">
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
            onClick={toggleSummary}
          >
            {isSummaryExpanded ? (
              // 펼쳐진 상태
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">수입</span>
                  <span className="text-base font-semibold text-income-fixed">
                    +{totalIncome.toLocaleString()}원
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">지출</span>
                  <span className="text-base font-semibold text-expense-fixed">
                    -{totalExpense.toLocaleString()}원
                  </span>
                </div>

                <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">합계</span>
                  <span className={`text-base font-semibold ${
                    balance >= 0 ? 'text-income-fixed' : 'text-expense-fixed'
                  }`}>
                    {balance >= 0 ? '+' : ''}{balance.toLocaleString()}원
                  </span>
                </div>

                {/* 하단 중앙에 접기 버튼 */}
                <div className="flex items-center justify-center gap-1 pt-2 text-xs text-gray-400 dark:text-gray-500 opacity-60">
                  <span>▲</span>
                  <span>접기</span>
                </div>
              </div>
            ) : (
              // 접힌 상태
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-base font-semibold text-income-fixed">
                    +{totalIncome.toLocaleString()}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500">/</span>
                  <span className="text-base font-semibold text-expense-fixed">
                    -{totalExpense.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>더보기</span>
                  <span>▼</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 거래 목록/달력 */}
      <div className="pb-20">
        {loading && (
          <div className="flex justify-center items-center py-16">
            <div className="text-gray-500 dark:text-gray-400">로딩 중...</div>
          </div>
        )}

        {error && (
          <div className="flex justify-center items-center py-16">
            <div className="text-red-600 dark:text-red-400">에러: {error.message}</div>
          </div>
        )}

        {!loading && !error && (
          <>
            {viewMode === 'list' ? (
              <TransactionList
                transactions={currentMonthTransactions}
                paymentMethods={paymentMethods}
                users={users}
                categories={allCategories}
                displayMode={displayMode}
                onTransactionLongPress={handleTransactionLongPress}
              />
            ) : (
              <div className="px-4">
                <div className="max-w-4xl mx-auto">
                  <TransactionCalendar
                    currentYear={year}
                    currentMonth={month}
                    transactions={currentMonthTransactions}
                    onDateClick={handleDateClick}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mock 모드 표시 */}
      {isMockMode() && (
        <div className="px-4 pb-4">
          <div className="max-w-4xl mx-auto bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-1 text-sm">
              🔧 Mock 모드
            </h3>
            <p className="text-xs text-yellow-800 dark:text-yellow-300">
              테스트 데이터를 사용 중입니다.
            </p>
          </div>
        </div>
      )}

      {/* 필터 모달 */}
      <TransactionFilterModal
        isOpen={showFilterModal}
        currentFilter={userFilter}
        onApply={(filter) => setUserFilter(filter)}
        onClose={() => setShowFilterModal(false)}
      />

      {/* 달력 날짜 클릭 모달 */}
      <DailyTransactionsModal
        isOpen={selectedDate !== null}
        date={selectedDate}
        transactions={selectedDateTransactions}
        paymentMethods={paymentMethods}
        users={users}
        categories={allCategories}
        onClose={() => {
          setSelectedDate(null)
          setSelectedDateTransactions([])
        }}
        onTransactionLongPress={handleTransactionLongPress}
      />

      {/* 거래 상세/수정 모달 */}
      <TransactionDetailModal
        isOpen={isDetailModalOpen}
        transaction={selectedTransaction}
        onClose={handleCloseDetailModal}
        onDeleted={() => window.location.reload()}
      />
    </div>
  )
}
